import { readChatResponse } from "@workspace/api-client-react";
import { QueryClient, QueryFunction } from "@tanstack/react-query";

export const ADMIN_MFA_REQUIRED_EVENT = "zeastro:admin-mfa-required";

export type ApiRequestError = Error & {
  status?: number;
  code?: string;
  retryAfterMs?: number;
  errors?: Record<string, string[]>;
  organization?: { id: string; name: string; plan: string };
  allowedAction?: string;
  continuation?: {
    source: "personal" | "organization";
    consultationType?: "human" | "ai";
    organizationId?: string;
    organizationName?: string;
    ownerPath?: string;
    requestPath?: string;
    upgradePath?: string;
    resetAt?: string;
    personalBillingBlocked?: boolean;
  };
};

const readableFieldName = (field: string) =>
  field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

/**
 * Returns server-provided details only for client errors. This keeps validation
 * failures actionable without leaking internal 5xx messages into the UI.
 */
export function getActionableApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  const apiError = error as ApiRequestError;
  if (
    typeof apiError?.status !== "number" ||
    apiError.status < 400 ||
    apiError.status >= 500
  ) {
    return fallback;
  }

  const fieldDetails = Object.entries(apiError.errors ?? {})
    .flatMap(([field, messages]) =>
      Array.isArray(messages)
        ? messages
            .filter((message): message is string => typeof message === "string" && message.trim().length > 0)
            .map((message) => `${readableFieldName(field)}: ${message.trim()}`)
        : [],
    )
    .slice(0, 5);
  const message =
    typeof apiError.message === "string" ? apiError.message.trim() : "";

  if (fieldDetails.length > 0) {
    return `${message || "Please check your information"} — ${fieldDetails.join("; ")}`;
  }
  return message || fallback;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    let message = text;
    let retryAfterMs: number | undefined;
    let requiredTier: string | undefined;
    let currentTier: string | undefined;
    let code: string | undefined;
    let upgradeTier: string | undefined;
    let featureKey: string | undefined;
    let limit: number | undefined;
    let used: number | undefined;
    let errors: Record<string, string[]> | undefined;
    let organization: { id: string; name: string; plan: string } | undefined;
    let allowedAction: string | undefined;
    let continuation: ApiRequestError["continuation"];
    try {
      const json = JSON.parse(text);
      // Quota-exceeded bodies use `error` instead of `message`.
      if (json.message) message = json.message;
      else if (json.error) message = json.error;
      if (json.requiredTier) requiredTier = json.requiredTier;
      if (json.currentTier) currentTier = json.currentTier;
      if (json.code) code = json.code;
      if (typeof json.retryAfterMs === "number" && Number.isFinite(json.retryAfterMs)) {
        retryAfterMs = Math.max(0, Math.round(json.retryAfterMs));
      }
      if (json.upgradeTier) upgradeTier = json.upgradeTier;
      if (json.featureKey) featureKey = json.featureKey;
      if (typeof json.limit === "number") limit = json.limit;
      if (typeof json.used === "number") used = json.used;
      if (json.errors && typeof json.errors === "object" && !Array.isArray(json.errors)) {
        errors = json.errors;
      }
      if (
        json.organization &&
        typeof json.organization === "object" &&
        typeof json.organization.id === "string" &&
        typeof json.organization.name === "string" &&
        typeof json.organization.plan === "string"
      ) {
        organization = {
          id: json.organization.id,
          name: json.organization.name,
          plan: json.organization.plan,
        };
      }
      if (typeof json.allowedAction === "string") allowedAction = json.allowedAction;
      if (
        json.continuation &&
        typeof json.continuation === "object" &&
        (json.continuation.source === "personal" || json.continuation.source === "organization")
      ) {
        continuation = {
          source: json.continuation.source,
          ...(json.continuation.consultationType === "human" || json.continuation.consultationType === "ai"
            ? { consultationType: json.continuation.consultationType }
            : {}),
          ...(typeof json.continuation.organizationId === "string" ? { organizationId: json.continuation.organizationId } : {}),
          ...(typeof json.continuation.organizationName === "string" ? { organizationName: json.continuation.organizationName } : {}),
          ...(typeof json.continuation.ownerPath === "string" ? { ownerPath: json.continuation.ownerPath } : {}),
          ...(typeof json.continuation.requestPath === "string" ? { requestPath: json.continuation.requestPath } : {}),
          ...(typeof json.continuation.upgradePath === "string" ? { upgradePath: json.continuation.upgradePath } : {}),
          ...(typeof json.continuation.resetAt === "string" ? { resetAt: json.continuation.resetAt } : {}),
          ...(typeof json.continuation.personalBillingBlocked === "boolean"
            ? { personalBillingBlocked: json.continuation.personalBillingBlocked }
            : {}),
        };
      }
    } catch {}
    if (retryAfterMs === undefined) {
      const retryAfter = Number.parseFloat(res.headers.get("Retry-After") ?? "");
      if (Number.isFinite(retryAfter)) retryAfterMs = Math.max(0, Math.round(retryAfter * 1_000));
    }
    if (code === "CSRF_VALIDATION_FAILED") {
      message = "Your secure session expired or changed. Refresh the app and try again.";
    }
    const error: any = new Error(message);
    error.status = res.status;
    if (requiredTier) error.requiredTier = requiredTier;
    if (currentTier) error.currentTier = currentTier;
    if (code) error.code = code;
    if (retryAfterMs !== undefined) error.retryAfterMs = retryAfterMs;
    if (upgradeTier) error.upgradeTier = upgradeTier;
    if (featureKey) error.featureKey = featureKey;
    if (limit !== undefined) error.limit = limit;
    if (used !== undefined) error.used = used;
    if (errors) error.errors = errors;
    if (organization) error.organization = organization;
    if (allowedAction) error.allowedAction = allowedAction;
    if (continuation) error.continuation = continuation;
    throw error;
  }
}

function getStoredLanguage(): string {
  try {
    const v = typeof localStorage !== "undefined" ? localStorage.getItem("cosmic-language") : null;
    return v && /^[a-z]{2,4}$/.test(v) ? v : "en";
  } catch {
    return "en";
  }
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
let csrfTokenRequest: Promise<string | undefined> | null = null;

async function getCsrfToken(): Promise<string | undefined> {
  if (csrfTokenRequest) return csrfTokenRequest;
  const request = (async () => {
    try {
      const response = await fetch("/api/auth/csrf", {
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) return undefined;
      const payload = await response.json() as { csrfToken?: unknown };
      return typeof payload.csrfToken === "string" ? payload.csrfToken : undefined;
    } catch {
      return undefined;
    }
  })();
  csrfTokenRequest = request;
  try {
    return await request;
  } finally {
    if (csrfTokenRequest === request) csrfTokenRequest = null;
  }
}

async function isCsrfValidationFailure(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  try {
    const payload = await response.clone().json() as { code?: unknown };
    return payload.code === "CSRF_VALIDATION_FAILED";
  } catch {
    return false;
  }
}

async function notifyAdminMfaRequired(response: Response): Promise<void> {
  if (response.status !== 428 || typeof window === "undefined") return;
  try {
    const payload = await response.clone().json() as { code?: unknown };
    if (payload.code === "ADMIN_MFA_REQUIRED") {
      window.dispatchEvent(new Event(ADMIN_MFA_REQUIRED_EVENT));
    }
  } catch {
    // Non-JSON failures still follow the caller's normal error handling.
  }
}

/**
 * Sends a request with the app's shared CSRF/session handling and returns the
 * response unchanged so flows that branch on specific HTTP statuses can do so.
 */
export async function apiRequestResponse(
  method: string,
  url: string,
  data?: unknown | undefined,
  options: Pick<RequestInit, "signal"> & { onDraft?: (text: string) => void } = {},
): Promise<Response> {
  const headers: Record<string, string> = data ? { "Content-Type": "application/json" } : {};
  headers["X-Language"] = getStoredLanguage();
  if (options.onDraft) headers.Accept = "text/event-stream";
  const requiresCsrf = UNSAFE_METHODS.has(method.toUpperCase());
  if (requiresCsrf) {
    const csrfToken = await getCsrfToken();
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  }
  try {
    const { isOptedOutSync } = await import("./telemetry");
    if (isOptedOutSync()) headers["X-Analytics-Opt-Out"] = "1";
  } catch {
    /* telemetry optional */
  }
  const requestInit: RequestInit = {
    signal: options.signal,
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  };
  let res = await fetch(url, requestInit);

  // A CSRF rejection happens before route handlers or mutations run. Refresh
  // the session-bound token and retry once; never retry other 403 responses
  // (such as the verified-account or suspended-account access boundaries).
  if (requiresCsrf && await isCsrfValidationFailure(res)) {
    const refreshedToken = await getCsrfToken();
    if (refreshedToken) {
      headers["X-CSRF-Token"] = refreshedToken;
      res = await fetch(url, { ...requestInit, headers: { ...headers } });
    }
  }

  if (options.onDraft) res = await readChatResponse(res, options.onDraft);
  await notifyAdminMfaRequired(res);
  return res;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options: Pick<RequestInit, "signal"> & { onDraft?: (text: string) => void } = {},
): Promise<Response> {
  const res = await apiRequestResponse(method, url, data, options);
  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      headers: { "X-Language": getStoredLanguage() },
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await notifyAdminMfaRequired(res);
    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
